import React, { useState, useEffect, useContext, useCallback } from 'react';
import { AppContext } from '@/App';
import axios from 'axios';
import {
  DollarSign, Search, CheckCircle, Loader2, X, AlertCircle,
  Package, Briefcase, ChevronUp, ChevronDown, FileText, Wallet,
  Clock, ShieldAlert, RotateCcw,
} from 'lucide-react';
import { toast } from 'sonner';

const STATUS_COLORS = {
  Due: 'bg-amber-100 text-amber-700 border-amber-200',
  Upcoming: 'bg-blue-100 text-blue-700 border-blue-200',
  Hidden: 'bg-gray-100 text-gray-700 border-gray-200',
  'Partially Paid': 'bg-blue-100 text-blue-700 border-blue-200',
  Paid: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  'Refund Pending': 'bg-rose-100 text-rose-700 border-rose-200',
  'Refund Received': 'bg-purple-100 text-purple-700 border-purple-200',
};

const REFUND_STATUSES = ['Refund Pending', 'Refund Received'];
const isRefundRow = (p) => REFUND_STATUSES.includes(p.paymentStatus) || REFUND_STATUSES.includes(p.status);
const isHiddenRow = (p) => p.paymentStatus === 'Hidden';
const isPaidRow = (p) => (p.paymentStatus === 'Paid' || p.status === 'Paid') && !isRefundRow(p);
const isAdvanceRow = (p) => p.advanceSplitOrder !== undefined && p.advanceSplitOrder !== null;

export default function ProjectPayments() {
  const { API, user } = useContext(AppContext);
  const isAdmin = (user?.role || '') === 'Admin';

  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [poTypeFilter, setPoTypeFilter] = useState('all');
  const [activeTab, setActiveTab] = useState('due'); // 'upcoming' | 'due' | 'paid' | 'refunds'
  const [expandedId, setExpandedId] = useState(null);

  // Modal state — used by both legacy "Record Payment" and advance "Mark Paid"
  const [payModal, setPayModal] = useState(null);
  const [payForm, setPayForm] = useState({
    paidAmount: '', paymentDate: '', paymentMode: 'NEFT', referenceNumber: '', bankName: '',
  });
  const [paying, setPaying] = useState(false);

  const fetchPayments = useCallback(async () => {
    setLoading(true);
    try {
      const q = new URLSearchParams();
      if (poTypeFilter !== 'all') q.set('poType', poTypeFilter);
      const r = await axios.get(`${API}/projects/payments?${q.toString()}`);
      setPayments(r.data?.data || []);
    } catch (e) {
      toast.error('Failed to load Project Payments');
    } finally {
      setLoading(false);
    }
  }, [API, poTypeFilter]);

  useEffect(() => { fetchPayments(); }, [fetchPayments]);

  // ===== One-click GST backfill (admin) =====
  const [recomputingGst, setRecomputingGst] = useState(false);
  const handleRecomputeGst = async () => {
    if (!window.confirm(
      `Recompute GST on ALL legacy Project Payments?\n\n` +
      `Walks every non-advance payment row and refreshes\n` +
      `amount = base + GST (using each linked QA's gstRate).\n\n` +
      `Idempotent — safe to run multiple times. Already-correct rows are skipped.`
    )) return;
    setRecomputingGst(true);
    try {
      const r = await axios.post(`${API}/projects/payments/recompute-gst`);
      const { scanned, fixed, skipped, fullyPaidBumped, qaGstRateBackfilled } = r.data || {};
      const extra = [];
      if (fullyPaidBumped) extra.push(`${fullyPaidBumped} Paid rows uplifted`);
      if (qaGstRateBackfilled) extra.push(`${qaGstRateBackfilled} QA rows had gstRate backfilled`);
      toast.success(
        `GST recompute done — ${fixed}/${scanned} rows updated (${skipped} already correct)` +
        (extra.length ? ` · ${extra.join(', ')}` : '')
      );
      await fetchPayments();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Recompute failed');
    } finally {
      setRecomputingGst(false);
    }
  };

  // ===== Open modal =====
  const openPayModal = (p) => {
    setPayModal(p);
    const isAdv = isAdvanceRow(p);
    setPayForm({
      paidAmount: isAdv ? (p.invoiceAmount || 0) : (p.balanceAmount || 0),
      paymentDate: new Date().toISOString().split('T')[0],
      paymentMode: 'NEFT',
      referenceNumber: '',
      bankName: '',
    });
  };

  const submitPay = async (e) => {
    e.preventDefault();
    const amt = parseFloat(payForm.paidAmount);
    if (!amt || amt <= 0) { toast.error('Enter a valid amount'); return; }
    const isAdv = isAdvanceRow(payModal);
    if (!isAdv && amt > (payModal.balanceAmount || 0) + 0.01) {
      toast.error(`Cannot exceed balance Rs. ${payModal.balanceAmount}`);
      return;
    }
    setPaying(true);
    try {
      const body = isAdv ? {
        paymentDate: payForm.paymentDate,
        paymentAmount: amt,
        paymentMode: payForm.paymentMode,
        referenceNumber: payForm.referenceNumber,
        bankName: payForm.bankName,
      } : {
        paidAmount: amt,
        paymentDate: payForm.paymentDate,
      };
      await axios.put(`${API}/projects/payments/${payModal.id}/mark-paid`, body);
      toast.success(isAdv ? 'Advance payment marked as Paid' : 'Payment recorded');
      setPayModal(null);
      await fetchPayments();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to record payment');
    } finally {
      setPaying(false);
    }
  };

  // Diagnose modal — click "🔍 Diagnose PO" in the create form to inspect
  // ---- Refund modal state (Path B — Admin refund of any payment row) ----
  const [refundModal, setRefundModal] = useState(null); // payment row being refunded
  const [refundForm, setRefundForm] = useState({ amount: '', reason: '' });
  const [refunding, setRefunding] = useState(false);

  const openRefundModal = (p) => {
    setRefundModal(p);
    // Pre-fill with the paid amount (or invoice if not paid) — user can edit down for partial refund.
    const paid = parseFloat(p.paymentAmount) || parseFloat(p.paidAmount) || parseFloat(p.invoiceAmount) || parseFloat(p.amount) || 0;
    setRefundForm({ amount: paid, reason: '' });
  };

  const submitRefund = async (e) => {
    e.preventDefault();
    if (!refundModal) return;
    const amt = parseFloat(refundForm.amount);
    const cap = parseFloat(refundModal.paymentAmount) || parseFloat(refundModal.paidAmount) || parseFloat(refundModal.invoiceAmount) || parseFloat(refundModal.amount) || 0;
    if (!amt || amt <= 0) { toast.error('Enter a valid refund amount'); return; }
    if (amt > cap + 0.01) { toast.error(`Refund cannot exceed paid amount ${fmt(cap)}`); return; }
    if (!refundForm.reason.trim()) { toast.error('Refund reason is required'); return; }
    setRefunding(true);
    try {
      await axios.post(`${API}/projects/payments/${refundModal.id}/admin-refund`, {
        refundAmount: amt,
        reason: refundForm.reason.trim(),
      });
      toast.success(`Payment moved to Refunds — ${fmt(amt)}`);
      setRefundModal(null);
      setRefundForm({ amount: '', reason: '' });
      await fetchPayments();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Refund failed');
    } finally {
      setRefunding(false);
    }
  };

  // Legacy prompt-based fallback (kept for other code paths that may still call it)
  const handleAdminRefund = openRefundModal;


  // ===== Mark Refund Received =====
  const handleMarkRefundReceived = async (p) => {
    const bankRef = window.prompt(
      `Confirm refund RECEIVED for ${p.paymentNumber || p.id}?\n\n` +
      `Vendor: ${p.vendorName || '-'}\nAmount: Rs. ${p.invoiceAmount || p.amount || 0}\n\n` +
      `Bank reference / UTR (optional):`,
      ''
    );
    if (bankRef === null) return;
    try {
      await axios.post(`${API}/projects/payments/${p.id}/mark-refund-received`, {
        bankRef: bankRef || '',
      });
      toast.success('Refund marked as received');
      await fetchPayments();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to mark refund received');
    }
  };

  // ===== Filtering =====
  const filtered = payments
    .filter((p) => !isHiddenRow(p)) // never show Hidden advance rows
    .filter((p) => !searchTerm ||
      p.id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.grnNo?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.poNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.vendorName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.invoiceNo?.toLowerCase().includes(searchTerm.toLowerCase())
    )
    .filter((p) => {
      if (activeTab === 'due') {
        if (isRefundRow(p) || isPaidRow(p)) return false;
        if (p.paymentStatus === 'Due') return true;
        if (p.paymentStatus === 'Upcoming') return false;
        // Legacy: anything not Paid
        return (p.status || '') !== 'Paid';
      }
      if (activeTab === 'upcoming') {
        if (isRefundRow(p) || isPaidRow(p)) return false;
        return p.paymentStatus === 'Upcoming';
      }
      if (activeTab === 'paid') return isPaidRow(p);
      if (activeTab === 'refunds') return isRefundRow(p);
      return true;
    });

  // ===== Stats (over ALL non-hidden visible payments, ignoring search/tab) =====
  const _visible = payments.filter((p) => !isHiddenRow(p));
  const tabStats = {
    upcoming: _visible.filter((p) => p.paymentStatus === 'Upcoming' && !isRefundRow(p) && !isPaidRow(p)),
    due: _visible.filter((p) => {
      if (isRefundRow(p) || isPaidRow(p)) return false;
      if (p.paymentStatus === 'Due') return true;
      if (p.paymentStatus === 'Upcoming') return false;
      return (p.status || '') !== 'Paid';
    }),
    paid: _visible.filter((p) => isPaidRow(p)),
    refunds: _visible.filter((p) => isRefundRow(p)),
  };

  const fmt = (n) => `Rs. ${Math.round(Number(n) || 0).toLocaleString('en-IN')}`;

  // ---- Single source of truth for the amount displayed in each tab. ----
  // Prevents summary card ↔ list-row drift (the bug that showed up in
  // Stores Payments where card used `paymentAmount` and list used
  // `invoiceAmount`, so partial payments / null paymentAmount rows made
  // the two totals disagree).
  //   - Refunds / Upcoming / Due    → invoice value
  //   - Paid (Completed)            → actually-paid amount, fallback to
  //                                    invoice for legacy rows
  const invoiceOf = (p) => parseFloat(p.invoiceAmount || p.amount || 0) || 0;
  const paidOf = (p) => {
    const paid = parseFloat(p.paymentAmount);
    if (!Number.isNaN(paid) && paid > 0) return paid;
    const legacyPaid = parseFloat(p.paidAmount);
    if (!Number.isNaN(legacyPaid) && legacyPaid > 0) return legacyPaid;
    return invoiceOf(p);
  };
  const rowAmountFor = (p, tab) => tab === 'paid' ? paidOf(p) : invoiceOf(p);

  const amtOf = (p) => rowAmountFor(p, activeTab);
  const balanceOf = (p) => isAdvanceRow(p) ? (isPaidRow(p) ? 0 : invoiceOf(p)) : (parseFloat(p.balanceAmount || 0) || 0);

  const tabAmount = (rows, tab) => rows.reduce((s, p) => s + rowAmountFor(p, tab), 0);
  const paidTotal = tabAmount(tabStats.paid, 'paid');
  const refundsTotal = tabAmount(tabStats.refunds, 'refunds');

  return (
    <div className="space-y-6" data-testid="project-payments-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Project Payments</h1>
          <p className="text-gray-500 mt-1">Auto-created from Advance Payment splits & QA approvals · isolated from Stores</p>
        </div>
        <div className="flex items-center gap-4">
          {isAdmin && (
            <button
              onClick={handleRecomputeGst}
              disabled={recomputingGst}
              className="px-3 py-2 bg-amber-600 text-white text-xs font-semibold rounded-lg hover:bg-amber-700 disabled:opacity-60 flex items-center gap-1.5"
              data-testid="project-payments-recompute-gst-btn"
              title="One-time fix: walks every legacy Project Payment row and refreshes amount = base + GST"
            >
              {recomputingGst ? <Loader2 size={14} className="animate-spin" /> : '↻'} Recompute GST
            </button>
          )}
          <div className="text-right">
            <p className="text-xs text-gray-500 uppercase tracking-wider">Total Due</p>
            <p className="text-2xl font-bold text-rose-600 tabular-nums">{fmt(tabAmount(tabStats.due, 'due'))}</p>
          </div>
        </div>
      </div>

      {/* Status tabs (4) — mirror of Stores Payments */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="project-payments-status-tabs">
        <button
          onClick={() => setActiveTab('upcoming')}
          className={`text-left bg-white rounded-xl p-4 border-2 transition ${
            activeTab === 'upcoming' ? 'border-blue-500 ring-2 ring-blue-100' : 'border-gray-200 hover:border-blue-300'
          }`}
          data-testid="project-payments-tab-upcoming"
        >
          <div className="flex items-center justify-between">
            <Clock size={18} className="text-blue-500" />
            <span className="text-xs font-semibold text-blue-600 tabular-nums">{tabStats.upcoming.length}</span>
          </div>
          <p className="text-sm text-gray-600 mt-2">Upcoming</p>
          <p className="text-lg font-bold text-blue-700 tabular-nums">{fmt(tabAmount(tabStats.upcoming, 'upcoming'))}</p>
        </button>
        <button
          onClick={() => setActiveTab('due')}
          className={`text-left bg-white rounded-xl p-4 border-2 transition ${
            activeTab === 'due' ? 'border-amber-500 ring-2 ring-amber-100' : 'border-gray-200 hover:border-amber-300'
          }`}
          data-testid="project-payments-tab-due"
        >
          <div className="flex items-center justify-between">
            <AlertCircle size={18} className="text-amber-500" />
            <span className="text-xs font-semibold text-amber-600 tabular-nums">{tabStats.due.length}</span>
          </div>
          <p className="text-sm text-gray-600 mt-2">Due</p>
          <p className="text-lg font-bold text-amber-700 tabular-nums">{fmt(tabAmount(tabStats.due, 'due'))}</p>
        </button>
        <button
          onClick={() => setActiveTab('paid')}
          className={`text-left bg-white rounded-xl p-4 border-2 transition ${
            activeTab === 'paid' ? 'border-emerald-500 ring-2 ring-emerald-100' : 'border-gray-200 hover:border-emerald-300'
          }`}
          data-testid="project-payments-tab-paid"
        >
          <div className="flex items-center justify-between">
            <CheckCircle size={18} className="text-emerald-500" />
            <span className="text-xs font-semibold text-emerald-600 tabular-nums">{tabStats.paid.length}</span>
          </div>
          <p className="text-sm text-gray-600 mt-2">Completed</p>
          <p className="text-lg font-bold text-emerald-700 tabular-nums">{fmt(paidTotal)}</p>
        </button>
        <button
          onClick={() => setActiveTab('refunds')}
          className={`text-left bg-white rounded-xl p-4 border-2 transition ${
            activeTab === 'refunds' ? 'border-rose-500 ring-2 ring-rose-100' : 'border-gray-200 hover:border-rose-300'
          }`}
          data-testid="project-payments-tab-refunds"
        >
          <div className="flex items-center justify-between">
            <RotateCcw size={18} className="text-rose-500" />
            <span className="text-xs font-semibold text-rose-600 tabular-nums">{tabStats.refunds.length}</span>
          </div>
          <p className="text-sm text-gray-600 mt-2">Refunds</p>
          <p className="text-lg font-bold text-rose-700 tabular-nums">{fmt(refundsTotal)}</p>
        </button>
      </div>

      {/* Material / Service sub-tabs */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="flex">
          <button
            onClick={() => setPoTypeFilter('all')}
            className={`flex-1 px-4 py-2.5 text-sm font-semibold transition border-b-2 ${
              poTypeFilter === 'all' ? 'border-gray-700 text-gray-800 bg-gray-50' : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
            data-testid="project-payments-pt-all"
          >
            All
          </button>
          <button
            onClick={() => setPoTypeFilter('material')}
            className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-semibold transition border-b-2 ${
              poTypeFilter === 'material' ? 'border-amber-500 text-amber-700 bg-amber-50' : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
            data-testid="project-material-payments-tab"
          >
            <Package size={16} /> Material
          </button>
          <button
            onClick={() => setPoTypeFilter('service')}
            className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-semibold transition border-b-2 ${
              poTypeFilter === 'service' ? 'border-indigo-500 text-indigo-700 bg-indigo-50' : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
            data-testid="project-service-payments-tab"
          >
            <Briefcase size={16} /> Service
          </button>
        </div>
      </div>

      {/* Search */}
      <div className="relative">
        <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
        <input
          type="text"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Search by Payment ID, GRN, PO, Vendor, Invoice..."
          className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:border-[#1a237e] outline-none bg-white shadow-sm"
          data-testid="project-payments-search"
        />
      </div>

      {/* Payment cards */}
      <div className="space-y-3">
        {loading ? (
          <div className="bg-white rounded-xl border border-gray-200 p-12 text-center text-gray-400">
            <Loader2 className="animate-spin mx-auto mb-2" /> Loading…
          </div>
        ) : filtered.length === 0 ? (
          <div className="bg-white rounded-xl border border-gray-200 p-12 text-center text-gray-500">
            <DollarSign className="mx-auto mb-2 text-gray-300" size={36} />
            No payments in this view.
            {activeTab === 'upcoming' && (
              <p className="text-xs mt-2 text-gray-400">Upcoming = Advance Payment splits waiting for the day-window to expire after all GRNs+QAs are done.</p>
            )}
            {activeTab === 'refunds' && (
              <p className="text-xs mt-2 text-gray-400">Refunds = Auto-flagged from QA rejection, or manually initiated by Admin.</p>
            )}
          </div>
        ) : filtered.map((p) => (
          <div key={p.id} className={`bg-white rounded-xl border shadow-sm overflow-hidden ${
            isAdvanceRow(p) ? 'border-l-4 border-l-amber-500 border-gray-200' : 'border-gray-200'
          }`}>
            {/* Card header */}
            <div
              className="flex items-center justify-between px-5 py-3.5 cursor-pointer hover:bg-gray-50/60 transition"
              onClick={() => setExpandedId(expandedId === p.id ? null : p.id)}
              data-testid={`project-payment-card-${p.id}`}
            >
              <div className="flex items-center gap-4 flex-1 min-w-0">
                <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium border ${
                  STATUS_COLORS[p.paymentStatus || p.status] || 'bg-gray-100 text-gray-700 border-gray-200'
                }`}>
                  {p.paymentStatus || p.status}
                </span>
                {isAdvanceRow(p) && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-50 text-amber-800 border border-amber-200 rounded text-[10px] font-semibold uppercase">
                    <Wallet size={10} /> Advance {p.advanceSplitOrder}/{p.advanceTotalSplits} · {p.advanceSplitPercent}%
                  </span>
                )}
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-sm font-semibold text-[#1a237e]">{p.paymentNumber || p.id}</span>
                    <span className="text-gray-300">|</span>
                    <span className="text-sm font-medium text-gray-900 truncate">{p.vendorName || '-'}</span>
                    {p.poType === 'service' && (
                      <span className="px-1.5 py-0.5 bg-indigo-100 text-indigo-700 rounded text-[10px] font-medium uppercase">Service</span>
                    )}
                  </div>
                  <div className="flex items-center gap-3 mt-0.5 text-xs text-gray-500">
                    {p.grnNo && <span>GRN: {p.grnNo}</span>}
                    <span>PO: {p.poNumber || '-'}</span>
                    {p.invoiceNo && <span>Inv: {p.invoiceNo}</span>}
                    {p.dueDate && <span>Due: {p.dueDate}</span>}
                  </div>
                </div>
              </div>
              {/* Amounts + actions */}
              <div className="flex items-center gap-6">
                <div className="text-right">
                  <p className="text-[10px] text-gray-400 uppercase tracking-wider">Amount</p>
                  <p className="text-sm font-semibold text-gray-900 tabular-nums">{fmt(amtOf(p))}</p>
                </div>
                <div className="text-right">
                  <p className="text-[10px] text-gray-400 uppercase tracking-wider">Balance</p>
                  <p className="text-sm font-bold text-rose-600 tabular-nums">{fmt(balanceOf(p))}</p>
                </div>
                <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                  {/* Action: pay / refund-receive / admin-refund */}
                  {p.paymentStatus === 'Refund Pending' ? (
                    <button
                      onClick={() => handleMarkRefundReceived(p)}
                      className="px-3 py-1.5 bg-purple-600 text-white rounded-lg text-xs font-medium hover:bg-purple-700"
                      data-testid={`project-payment-mark-refund-received-${p.id}`}
                    >
                      Mark Refund Received
                    </button>
                  ) : isPaidRow(p) ? (
                    <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-emerald-50 text-emerald-700 rounded-lg text-xs font-medium">
                      <CheckCircle size={14} /> Paid
                    </span>
                  ) : p.paymentStatus === 'Upcoming' ? (
                    <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-blue-50 text-blue-700 rounded-lg text-xs font-medium">
                      <Clock size={14} /> Waiting
                    </span>
                  ) : (
                    <button
                      onClick={() => openPayModal(p)}
                      className="px-3 py-1.5 bg-violet-600 text-white rounded-lg text-xs font-medium hover:bg-violet-700"
                      data-testid={`project-payment-pay-${p.id}`}
                    >
                      {isAdvanceRow(p) ? 'Mark Paid' : 'Record Payment'}
                    </button>
                  )}
                  {/* Admin-only Refund button — visible on ANY non-refund, non-received row (including Paid rows in the Completed tab, so historical payments can be refunded on dispute). */}
                  {isAdmin && !isRefundRow(p) && (p.paymentStatus !== 'Hidden') && (
                    <button
                      onClick={() => openRefundModal(p)}
                      className="px-2 py-1.5 bg-rose-50 text-rose-700 border border-rose-200 rounded-lg text-xs font-medium hover:bg-rose-100"
                      title="Admin: initiate a refund for this payment (partial or full)"
                      data-testid={`project-payment-admin-refund-${p.id}`}
                    >
                      <ShieldAlert size={12} className="inline mr-0.5" /> Refund
                    </button>
                  )}
                  {expandedId === p.id
                    ? <ChevronUp size={18} className="text-gray-400" />
                    : <ChevronDown size={18} className="text-gray-400" />}
                </div>
              </div>
            </div>

            {/* Expanded details */}
            {expandedId === p.id && (
              <div className="border-t border-gray-100 px-5 py-4 bg-gray-50/30 space-y-3">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  <div><p className="text-xs text-gray-500">Payment ID</p><p className="font-medium">{p.paymentNumber || p.id}</p></div>
                  {p.grnNo && <div><p className="text-xs text-gray-500">GRN</p><p className="font-medium">{p.grnNo}</p></div>}
                  <div><p className="text-xs text-gray-500">PO Number</p><p className="font-medium">{p.poNumber || '-'}</p></div>
                  {p.invoiceNo && <div><p className="text-xs text-gray-500">Invoice No</p><p className="font-medium">{p.invoiceNo}</p></div>}
                  <div><p className="text-xs text-gray-500">Vendor</p><p className="font-medium">{p.vendorName || '-'}</p></div>
                  <div><p className="text-xs text-gray-500">Vendor GST</p><p className="font-medium">{p.vendorGst || '-'}</p></div>
                  <div><p className="text-xs text-gray-500">Type</p><p className="font-medium">{p.poType === 'service' ? 'Service' : 'Material'}</p></div>
                  <div><p className="text-xs text-gray-500">Due Date</p><p className="font-medium">{p.dueDate || '-'}</p></div>
                  <div><p className="text-xs text-gray-500">Project</p><p className="font-medium">{p.projectName || '-'}</p></div>
                  <div><p className="text-xs text-gray-500">Status</p><p className="font-medium">{p.paymentStatus || p.status}</p></div>
                  {(p.baseAmount !== undefined || p.gstAmount !== undefined) && (
                    <>
                      <div><p className="text-xs text-gray-500">Base Amount</p><p className="font-medium tabular-nums">{fmt(p.baseAmount)}</p></div>
                      <div><p className="text-xs text-gray-500">GST Amount</p><p className="font-medium tabular-nums text-amber-700">{fmt(p.gstAmount)}</p></div>
                    </>
                  )}
                  {isAdvanceRow(p) && (
                    <>
                      <div><p className="text-xs text-gray-500">Split Trigger</p><p className="font-medium">{p.advanceSplitTrigger}{p.advanceDaysAfterGRN ? ` (${p.advanceDaysAfterGRN}d after GRN)` : ''}</p></div>
                      <div><p className="text-xs text-gray-500">Fulfillment</p><p className="font-medium">{p.fulfillmentStatus || 'Pending'}</p></div>
                    </>
                  )}
                </div>

                {/* Refund banner */}
                {isRefundRow(p) && (
                  <div className="bg-rose-50 border border-rose-200 rounded-lg p-3 text-sm" data-testid={`project-payment-refund-banner-${p.id}`}>
                    <div className="flex items-center gap-2 text-rose-700 font-semibold">
                      <RotateCcw size={14} /> Refund — {p.paymentStatus}
                    </div>
                    {p.refundReason && <div className="text-xs text-rose-600 mt-1">Reason: {p.refundReason}</div>}
                    {p.refundAmount != null && (
                      <div className="text-xs text-rose-700 mt-1">
                        Refund Amount: <strong>{fmt(p.refundAmount)}</strong>
                        {parseFloat(p.refundAmount) < (parseFloat(p.paymentAmount) || parseFloat(p.paidAmount) || parseFloat(p.invoiceAmount) || 0) - 0.01 && (
                          <span className="ml-2 px-1.5 py-0.5 bg-rose-100 text-rose-800 rounded text-[10px] font-semibold">PARTIAL</span>
                        )}
                      </div>
                    )}
                    {p.refundInitiatedAt && <div className="text-xs text-rose-600">Initiated: {p.refundInitiatedAt}{p.refundInitiatedBy ? ` by ${p.refundInitiatedBy}` : ''}</div>}
                    {p.refundReceivedAt && <div className="text-xs text-purple-700">Received: {p.refundReceivedAt} {p.refundBankRef && `(UTR/Ref: ${p.refundBankRef})`}</div>}
                  </div>
                )}

                {/* Legacy payment history */}
                {Array.isArray(p.paymentHistory) && p.paymentHistory.length > 0 && (
                  <div className="pt-2 border-t border-gray-200">
                    <p className="text-xs font-semibold text-gray-600 mb-2 flex items-center gap-1"><FileText size={12} /> Payment History</p>
                    <div className="space-y-1">
                      {p.paymentHistory.map((h, i) => (
                        <div key={i} className="flex justify-between text-xs text-gray-700 bg-white border border-gray-100 rounded px-3 py-1.5">
                          <span>{h.paymentDate || h.date}</span>
                          <span className="font-medium text-emerald-700 tabular-nums">{fmt(h.paidAmount || h.amount)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Pay modal */}
      {payModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <form onSubmit={submitPay} className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
            <div className="p-5 border-b border-gray-200 bg-violet-600 rounded-t-2xl flex items-center justify-between">
              <div>
                <h2 className="text-lg font-bold text-white">
                  {isAdvanceRow(payModal) ? 'Mark Advance Paid' : 'Record Payment'}
                </h2>
                <p className="text-xs text-white/80">{payModal.paymentNumber || payModal.id} · {payModal.vendorName}</p>
              </div>
              <button type="button" onClick={() => setPayModal(null)} className="text-white/80 hover:bg-white/10 p-2 rounded-lg"><X size={18} /></button>
            </div>
            <div className="p-5 space-y-3">
              <div className="bg-gray-50 rounded-lg p-3 text-sm space-y-1">
                <div className="flex justify-between"><span className="text-gray-600">Total Amount:</span><span className="font-semibold tabular-nums">{fmt(amtOf(payModal))}</span></div>
                {!isAdvanceRow(payModal) && (
                  <>
                    <div className="flex justify-between"><span className="text-gray-600">Already Paid:</span><span className="tabular-nums">{fmt(payModal.paidAmount)}</span></div>
                    <div className="flex justify-between text-rose-600 font-semibold"><span>Balance:</span><span className="tabular-nums">{fmt(payModal.balanceAmount)}</span></div>
                  </>
                )}
                {isAdvanceRow(payModal) && (
                  <div className="flex justify-between text-amber-700"><span>Split:</span><span>{payModal.advanceSplitOrder}/{payModal.advanceTotalSplits} ({payModal.advanceSplitPercent}%)</span></div>
                )}
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600 uppercase">Pay Amount *</label>
                <input
                  type="number"
                  step="0.01"
                  min="0.01"
                  required
                  value={payForm.paidAmount}
                  onChange={(e) => setPayForm((f) => ({ ...f, paidAmount: e.target.value }))}
                  className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  data-testid="project-payment-amount-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-medium text-gray-600 uppercase">Payment Date</label>
                  <input
                    type="date"
                    value={payForm.paymentDate}
                    onChange={(e) => setPayForm((f) => ({ ...f, paymentDate: e.target.value }))}
                    className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  />
                </div>
                {isAdvanceRow(payModal) && (
                  <div>
                    <label className="text-xs font-medium text-gray-600 uppercase">Mode</label>
                    <select
                      value={payForm.paymentMode}
                      onChange={(e) => setPayForm((f) => ({ ...f, paymentMode: e.target.value }))}
                      className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    >
                      <option>NEFT</option><option>RTGS</option><option>IMPS</option>
                      <option>Cheque</option><option>UPI</option><option>Cash</option>
                    </select>
                  </div>
                )}
              </div>
              {isAdvanceRow(payModal) && (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-medium text-gray-600 uppercase">Ref / UTR</label>
                    <input
                      type="text"
                      value={payForm.referenceNumber}
                      onChange={(e) => setPayForm((f) => ({ ...f, referenceNumber: e.target.value }))}
                      className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                      placeholder="UTR / Cheque #"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-600 uppercase">Bank</label>
                    <input
                      type="text"
                      value={payForm.bankName}
                      onChange={(e) => setPayForm((f) => ({ ...f, bankName: e.target.value }))}
                      className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                      placeholder="HDFC / SBI / ..."
                    />
                  </div>
                </div>
              )}
            </div>
            <div className="p-4 border-t border-gray-200 bg-gray-50 rounded-b-2xl flex justify-end gap-2">
              <button type="button" onClick={() => setPayModal(null)} className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg">Cancel</button>
              <button
                type="submit"
                disabled={paying}
                className="px-5 py-2 bg-violet-600 text-white rounded-lg hover:bg-violet-700 flex items-center gap-2 disabled:opacity-60"
                data-testid="project-payment-submit-btn"
              >
                {paying ? <Loader2 size={16} className="animate-spin" /> : 'Record'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Refund modal — Admin refund a Paid (or any) payment. Supports
          partial refunds by editing the pre-filled paid amount. */}
      {refundModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <form onSubmit={submitRefund} className="bg-white rounded-2xl shadow-2xl w-full max-w-md" data-testid="project-refund-modal">
            <div className="p-5 border-b border-gray-200 bg-rose-600 rounded-t-2xl flex items-center justify-between">
              <div>
                <h2 className="text-lg font-bold text-white">Initiate Refund</h2>
                <p className="text-xs text-white/80">{refundModal.paymentNumber || refundModal.id} · {refundModal.vendorName}</p>
              </div>
              <button type="button" onClick={() => setRefundModal(null)} className="text-white/80 hover:bg-white/10 p-2 rounded-lg" data-testid="project-refund-close">
                <X size={18} />
              </button>
            </div>
            <div className="p-5 space-y-3">
              <div className="bg-gray-50 rounded-lg p-3 text-sm space-y-1">
                <div className="flex justify-between">
                  <span className="text-gray-600">Original Amount:</span>
                  <span className="font-semibold tabular-nums">{fmt(invoiceOf(refundModal))}</span>
                </div>
                {(parseFloat(refundModal.paymentAmount) || parseFloat(refundModal.paidAmount) || 0) > 0 && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Amount Paid:</span>
                    <span className="font-semibold tabular-nums">{fmt(parseFloat(refundModal.paymentAmount) || parseFloat(refundModal.paidAmount))}</span>
                  </div>
                )}
                <div className="flex justify-between text-rose-600 font-semibold pt-1 border-t border-gray-200">
                  <span>Status:</span><span>{refundModal.paymentStatus || refundModal.status}</span>
                </div>
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600 uppercase">Refund Amount <span className="text-rose-500">*</span></label>
                <input
                  type="number"
                  step="0.01"
                  min="0.01"
                  required
                  value={refundForm.amount}
                  onChange={(e) => setRefundForm((f) => ({ ...f, amount: e.target.value }))}
                  className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:border-rose-500 outline-none"
                  data-testid="project-refund-amount-input"
                />
                <p className="text-[10px] text-gray-500 mt-1">Pre-filled with paid amount. Reduce for partial refund (e.g. only 30% dispute).</p>
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600 uppercase">Refund Reason <span className="text-rose-500">*</span></label>
                <textarea
                  required
                  rows={3}
                  value={refundForm.reason}
                  onChange={(e) => setRefundForm((f) => ({ ...f, reason: e.target.value }))}
                  placeholder="e.g. Material found defective after installation / vendor short-shipped / QA post-approval failure"
                  className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:border-rose-500 outline-none resize-none"
                  data-testid="project-refund-reason-input"
                />
              </div>
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-2 text-xs text-amber-800">
                <ShieldAlert size={12} className="inline mr-1" />
                Row will move from Completed → Refunds tab as <strong>Refund Pending</strong>.
                Then use <strong>Mark Refund Received</strong> once the vendor returns the money.
              </div>
            </div>
            <div className="p-4 border-t border-gray-200 bg-gray-50 rounded-b-2xl flex justify-end gap-2">
              <button type="button" onClick={() => setRefundModal(null)} className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg">Cancel</button>
              <button
                type="submit"
                disabled={refunding}
                className="px-5 py-2 bg-rose-600 text-white rounded-lg hover:bg-rose-700 flex items-center gap-2 disabled:opacity-60"
                data-testid="project-refund-submit-btn"
              >
                {refunding ? <Loader2 size={16} className="animate-spin" /> : <>Initiate Refund</>}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
